// Shared atomic UI primitives — redesigned for the editorial-concierge
// system. Same exports as before (Av, Btn, Inp, Modal, Tabs) so every
// existing component picks up the new look without a rewrite. Plus
// new primitives the design needs (Empty, Skeleton, StatusPill,
// ProgressArc, SectionList, Mono, Serif).
//
// API rule: any prop / export name that existed in the previous file
// still exists with the same signature. Visual treatment + tokens
// flipped to the cream + persimmon + Plex system.
import { useState, useEffect, useRef } from "react";
import { T, T_NEW, colorFor } from "../theme.js";
import { I } from "../icons.jsx";

// ─── Av ─────────────────────────────────────────────────────────────
// Round avatar. Renders an uploaded image if `src` is provided; otherwise
// falls back to the first letter on a deterministic hashed color. Same
// signature as before. Image is cover-fit + circular-cropped so any
// photo works. onError flips local state so subsequent renders show
// initials instead of a broken image.
export function Av({ name, size = 44, src }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (src && !broken) {
    return (
      <img src={src} alt={name || ""} onError={() => setBroken(true)}
           style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover",
                    flexShrink: 0, background: colorFor(name) }} />
    );
  }
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: colorFor(name),
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: size * 0.4, fontWeight: 600, color: T_NEW.paperRaised,
                  flexShrink: 0, fontFamily: T_NEW.fontSans }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

// ─── Btn ────────────────────────────────────────────────────────────
// `variant`:
//   "accent"  → INK on cream (primary action — this is the design's
//               default; persimmon is reserved for the SECONDARY callout
//               line so it doesn't compete with the primary CTA)
//   "primary" → alias of "accent" (more semantic name; both supported)
//   "persimmon" → filled persimmon, for urgent CTAs only (e.g. "Решить")
//   "danger"  → deep crimson, destructive
//   "ghost"   → text-only, ink color, no background
//   "outline" → ink border, cream fill, ink text
// `full` makes the button stretch.
export function Btn({ children, onClick, variant = "accent", disabled, style: sx, full, type = "button" }) {
  const styleFor = (v) => {
    switch (v) {
      case "persimmon":
        return { background: T_NEW.persimmon, color: T_NEW.paperRaised, border: "none" };
      case "danger":
        return { background: T_NEW.danger, color: T_NEW.paperRaised, border: "none" };
      case "ghost":
        return { background: "transparent", color: T_NEW.ink, border: "none" };
      case "outline":
        return { background: T_NEW.paperRaised, color: T_NEW.ink, border: `1px solid ${T_NEW.ink}` };
      case "primary":
      case "accent":
      default:
        return { background: T_NEW.ink, color: T_NEW.paperRaised, border: "none" };
    }
  };
  const base = styleFor(variant);
  return (
    <button type={type} disabled={disabled} onClick={onClick}
            style={{ ...base,
                     borderRadius: T_NEW.r2,
                     padding: "10px 18px",
                     fontSize: T_NEW.body,
                     fontFamily: T_NEW.fontSans,
                     fontWeight: 600,
                     letterSpacing: "0.01em",
                     cursor: disabled ? "not-allowed" : "pointer",
                     opacity: disabled ? 0.4 : 1,
                     width: full ? "100%" : "auto",
                     transition: `background ${T_NEW.durBase} ${T_NEW.ease}, opacity ${T_NEW.durBase} ${T_NEW.ease}`,
                     ...sx }}>
      {children}
    </button>
  );
}

// ─── Inp ────────────────────────────────────────────────────────────
// Labeled input. Plex Mono uppercase label, ink-on-cream input.
// `error` prop highlights the field with a red ring + shows the message
// underneath — used by form validation states (Page 62).
export function Inp({ icon, label, type = "text", value, onChange,
                      placeholder, autoFocus, error, rightSlot,
                      inputMode, autoComplete }) {
  return (
    <div style={{ marginBottom: T_NEW.s4 }}>
      {label && (
        <label style={{
          fontSize: T_NEW.micro, color: T_NEW.ink56,
          fontFamily: T_NEW.fontMono, textTransform: "uppercase",
          letterSpacing: T_NEW.monoTrack, fontWeight: 500,
          marginBottom: T_NEW.s2, display: "block",
        }}>{label}</label>
      )}
      <div style={{
        display: "flex", alignItems: "center",
        background: T_NEW.paperRaised,
        borderRadius: T_NEW.r2,
        border: `1px solid ${error ? T_NEW.danger : T_NEW.ink12}`,
        padding: "0 14px",
        transition: `border-color ${T_NEW.durBase} ${T_NEW.ease}`,
      }}>
        {icon && <span style={{ marginRight: 10, display: "flex", color: T_NEW.ink56 }}>{icon}</span>}
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
               placeholder={placeholder} autoFocus={autoFocus}
               inputMode={inputMode} autoComplete={autoComplete}
               style={{ flex: 1, background: "transparent", border: "none", outline: "none",
                        color: T_NEW.ink, fontSize: T_NEW.bodyLg,
                        fontFamily: T_NEW.fontSans, padding: "12px 0" }} />
        {/* Right-side adornment — used by the sign-up nickname field
            to show a live ✓ / ✗ availability indicator next to the
            input without breaking the rest of the design. */}
        {rightSlot && (
          <span style={{ marginLeft: 10, display: "flex",
                         alignItems: "center", flexShrink: 0 }}>
            {rightSlot}
          </span>
        )}
      </div>
      {error && (
        <div style={{ fontSize: T_NEW.micro, color: T_NEW.danger, marginTop: T_NEW.s1, fontFamily: T_NEW.fontSans }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ─── useSwipeDownDismiss ────────────────────────────────────────────
// iOS-style swipe-down-to-close for a bottom-sheet modal body. Returns
// { ref, dy, dismissing } — attach ref to the scrollable modal body,
// translate by `translateY(${dy}px)`. The hook is a no-op on viewports
// wider than 640px so desktop modals keep their existing behavior.
//
// Gesture rules:
//   • touchstart records the start point and the body's scrollTop.
//   • touchmove claims the gesture as a dismiss-drag only when the
//     body is at scrollTop===0 AND the dominant movement is downward.
//     Once claimed, touchmove preventDefault stops the browser from
//     also scrolling the body. If the user instead scrolls down inside
//     the body, the gesture is "abandoned" and the rest of the touch
//     is passed through to native scroll.
//   • touchend: dy > 120px OR velocity > 0.5px/ms ⇒ animate out + close.
//     Otherwise transform snaps back to 0 via CSS transition.
export function useSwipeDownDismiss(onClose) {
  const ref = useRef(null);
  const [dy, setDy] = useState(0);
  const [dismissing, setDismissing] = useState(false);
  const dyRef = useRef(0);
  const stateRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(max-width: 640px)").matches) return;

    const writeDy = (v) => { dyRef.current = v; setDy(v); };

    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      stateRef.current = {
        startY: t.clientY,
        startX: t.clientX,
        startScroll: el.scrollTop,
        startTime: Date.now(),
        claimed: false,
      };
      writeDy(0);
    };

    const onMove = (e) => {
      const s = stateRef.current;
      if (!s) return;
      const t = e.touches[0];
      const dyRaw = t.clientY - s.startY;
      const dxRaw = t.clientX - s.startX;

      if (!s.claimed) {
        // Claim conditions: must be at the top of the scroll AND the
        // movement must be unambiguously downward and vertical.
        if (s.startScroll <= 0 &&
            dyRaw > 8 &&
            Math.abs(dyRaw) > Math.abs(dxRaw)) {
          s.claimed = true;
        } else if (Math.abs(dxRaw) > 12 || dyRaw < -8) {
          // It's a horizontal swipe or an upward scroll — release the
          // gesture entirely so native scrolling can take over for the
          // remainder of this touch sequence.
          stateRef.current = null;
          return;
        }
      }

      if (s.claimed === true) {
        e.preventDefault();
        writeDy(Math.max(0, dyRaw));
      }
    };

    const onEnd = () => {
      const s = stateRef.current;
      stateRef.current = null;
      if (!s || s.claimed !== true) {
        writeDy(0);
        return;
      }
      const finalDy = dyRef.current;
      const elapsed = Math.max(1, Date.now() - s.startTime);
      const velocity = finalDy / elapsed;
      if (finalDy > 120 || velocity > 0.5) {
        // Animate the sheet the rest of the way out, then close.
        setDismissing(true);
        writeDy(Math.max(window.innerHeight, finalDy + 200));
        setTimeout(() => onCloseRef.current?.(), 180);
      } else {
        writeDy(0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    // touchmove MUST be non-passive so preventDefault works to block
    // the browser's default scroll while we're owning the drag.
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  return { ref, dy, dismissing };
}

// ─── Modal ──────────────────────────────────────────────────────────
// Mobile (≤640): bottom-sheet pinned full-width. Desktop: centered card.
// CSS-driven via .cg-modal-* classes from App.jsx <style>.
// `tone="dark"` flips the body to ink (for wallet/scenario callouts).
//
// Mobile bottom-sheet variant supports swipe-down-to-dismiss via the
// useSwipeDownDismiss hook above — the grab handle is the visible
// affordance and the gesture also works from anywhere in the body when
// it's scrolled to the top.
export function Modal({ title, onClose, children, width = 420, tone = "paper" }) {
  const isDark = tone === "dark";
  const { ref: bodyRef, dy, dismissing } = useSwipeDownDismiss(onClose);
  const dragging = dy > 0;
  // Fade the backdrop in proportion to the drag — gives the user
  // visual feedback that they're tearing the sheet off. We clamp to
  // 0.05 so it doesn't go fully transparent.
  const scrim = dragging
    ? Math.max(0.05, 0.45 - dy / 900)
    : 0.45;
  return (
    <div className="cg-modal-bg"
         style={{ position: "fixed", inset: 0, display: "flex",
                  alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16,
                  backgroundColor: `rgba(20,17,13,${scrim})`,
                  transition: dragging ? "none" : "background-color 180ms",
                  pointerEvents: dismissing ? "none" : "auto" }}
         onClick={onClose}>
      <div className="cg-modal-body"
           ref={bodyRef}
           onClick={e => e.stopPropagation()}
           style={{ "--cg-modal-w": typeof width === "number" ? `${width}px` : width,
                    background: isDark ? T_NEW.inkRaised : T_NEW.paperRaised,
                    color: isDark ? T_NEW.paperOnInk : T_NEW.ink,
                    borderRadius: T_NEW.r3, padding: T_NEW.s6,
                    width: "var(--cg-modal-w)", maxWidth: "min(92vw, var(--cg-modal-w))",
                    maxHeight: "85dvh", overflowY: "auto", overflowX: "hidden",
                    boxShadow: T_NEW.shadowModal,
                    fontFamily: T_NEW.fontSans,
                    transform: `translateY(${dy}px)`,
                    transition: dragging
                      ? "none"
                      : "transform 220ms cubic-bezier(0.2,0,0.0,1.0)",
                    willChange: dragging ? "transform" : "auto" }}>
        {/* Grab handle — bottom-sheet affordance. CSS hides it on
            desktop via the .cg-modal-grab rule in App.jsx <style>. */}
        <div className="cg-modal-grab" aria-hidden />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: T_NEW.s4, gap: 12 }}>
          <h3 style={{
            fontSize: T_NEW.displaySm, fontWeight: 600,
            fontFamily: T_NEW.fontSerif,
            color: isDark ? T_NEW.paperOnInk : T_NEW.ink,
            margin: 0, letterSpacing: "-0.01em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            flex: 1, minWidth: 0,
          }}>{title}</h3>
          <button onClick={onClose} aria-label="Close"
                  style={{ background: "none", border: "none", cursor: "pointer",
                           padding: 6, display: "flex", flexShrink: 0,
                           color: isDark ? T_NEW.paperOnInk : T_NEW.ink }}>
            {I.close()}
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Tabs ───────────────────────────────────────────────────────────
// Horizontal-scroll tab strip. flex-1 per tab when they fit; overflow
// scrolls horizontally when they don't.
export function Tabs({ tabs, active, onSelect }) {
  return (
    <div
      className="cg-tabs-scroll"
      style={{
        display: "flex", gap: 2,
        background: T_NEW.paper,
        borderRadius: T_NEW.r2, padding: 3,
        marginBottom: T_NEW.s3,
        overflowX: "auto", scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        border: `1px solid ${T_NEW.ink12}`,
      }}
    >
      {tabs.map(t => (
        <button key={t.id} onClick={() => onSelect(t.id)}
                style={{
                  flex: "1 0 auto", minWidth: 88,
                  padding: "8px 14px",
                  border: "none", borderRadius: T_NEW.r1,
                  cursor: "pointer",
                  fontSize: T_NEW.body, fontWeight: 600,
                  whiteSpace: "nowrap",
                  fontFamily: T_NEW.fontSans,
                  background: active === t.id ? T_NEW.ink : "transparent",
                  color: active === t.id ? T_NEW.paperRaised : T_NEW.ink56,
                  transition: `background ${T_NEW.durBase} ${T_NEW.ease}`,
                }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── NEW PRIMITIVES (redesign-only) ─────────────────────────────────

// Mono: tiny utility wrapper for IBM Plex Mono UPPERCASE micro-text
// (status labels, timestamps, numerics, scenario labels in onboarding).
// Use sparingly — too much mono kills the editorial feel.
export function Mono({ children, size = T_NEW.micro, color = T_NEW.ink56, style }) {
  return (
    <span style={{
      fontFamily: T_NEW.fontMono,
      fontSize: size, color,
      letterSpacing: T_NEW.monoTrack,
      textTransform: "uppercase",
      fontWeight: 500,
      ...style,
    }}>{children}</span>
  );
}

// Serif: editorial display headings. Use for hero titles, price escalations,
// section headings ("Подборка дня").
export function Serif({ children, size = T_NEW.displayMd, color = T_NEW.ink, style, as: As = "span" }) {
  return (
    <As style={{
      fontFamily: T_NEW.fontSerif,
      fontSize: size, color, fontWeight: 400,
      letterSpacing: "-0.01em",
      lineHeight: 1.15,
      ...style,
    }}>{children}</As>
  );
}

// StatusPill: persimmon/bottle/ochre/ink chip for state labels.
// "ПРИНЯТ", "ВЫКУПЛЕН", "ДОСТАВЛЕН", "СП · ЦЕЛЬ", etc.
export function StatusPill({ children, tone = "ink" }) {
  const palette = {
    ink:       { bg: T_NEW.ink08,        fg: T_NEW.ink },
    persimmon: { bg: T_NEW.persimmonSoft,fg: T_NEW.persimmon },
    bottle:    { bg: T_NEW.bottleSoft,   fg: T_NEW.bottle },
    ochre:     { bg: T_NEW.ochreSoft,    fg: T_NEW.ochre },
    danger:    { bg: T_NEW.dangerSoft,   fg: T_NEW.danger },
  }[tone] || { bg: T_NEW.ink08, fg: T_NEW.ink };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: palette.bg, color: palette.fg,
      padding: "3px 8px", borderRadius: T_NEW.r1,
      fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
      fontWeight: 600, letterSpacing: T_NEW.monoTrack,
      textTransform: "uppercase", whiteSpace: "nowrap",
    }}>{children}</span>
  );
}

// ProgressArc: SVG arc used for group-buy progress (СП · ЦЕЛЬ 41/60).
// Stroke fills clockwise from top. Numerator/denominator centered in
// Plex Mono. `size` is the box; ring is ~85% of that.
export function ProgressArc({ value, total, size = 96, color = T_NEW.persimmon, trackColor = T_NEW.ink12, labelTop }) {
  const pct = Math.max(0, Math.min(1, total > 0 ? value / total : 0));
  const r = size * 0.42;
  const c = 2 * Math.PI * r;
  const dash = pct * c;
  return (
    <div style={{ position: "relative", width: size, height: size, fontFamily: T_NEW.fontMono }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
        <circle cx={size / 2} cy={size / 2} r={r}
                stroke={trackColor} strokeWidth="5" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r}
                stroke={color} strokeWidth="5" fill="none"
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={c * 0.25}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        {labelTop && (
          <div style={{ fontSize: T_NEW.micro, color: T_NEW.ink56, letterSpacing: T_NEW.monoTrack, textTransform: "uppercase", marginBottom: 2 }}>
            {labelTop}
          </div>
        )}
        <div style={{ fontFamily: T_NEW.fontSerif, fontSize: size * 0.26, color: T_NEW.ink, fontWeight: 400, lineHeight: 1 }}>
          {value}<span style={{ color: T_NEW.ink38 }}>/{total}</span>
        </div>
      </div>
    </div>
  );
}

// Empty: editorial empty-state. Big serif headline + supporting paragraph +
// optional primary CTA. Used for Лента / Заказы / Чаты / Кошелёк / Уведомления
// at zero state (Pages 59, 75-79). Caller can pass an icon node above the title.
export function Empty({ icon, title, body, cta, ctaOnClick, secondary, secondaryOnClick }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: `${T_NEW.s8}px ${T_NEW.s6}px`,
      textAlign: "center", color: T_NEW.ink,
      fontFamily: T_NEW.fontSans,
      gap: T_NEW.s3,
      maxWidth: 380, margin: "0 auto",
    }}>
      {icon && (
        <div style={{ color: T_NEW.persimmon, marginBottom: T_NEW.s2 }}>
          {icon}
        </div>
      )}
      {title && (
        <h2 style={{
          fontFamily: T_NEW.fontSerif, fontSize: T_NEW.displayMd,
          color: T_NEW.ink, fontWeight: 400, letterSpacing: "-0.01em",
          margin: 0, lineHeight: 1.15,
        }}>{title}</h2>
      )}
      {body && (
        <p style={{ color: T_NEW.ink56, fontSize: T_NEW.body, lineHeight: 1.55, margin: 0 }}>
          {body}
        </p>
      )}
      {(cta || secondary) && (
        <div style={{ display: "flex", gap: T_NEW.s2, marginTop: T_NEW.s3, flexWrap: "wrap", justifyContent: "center" }}>
          {cta && <Btn onClick={ctaOnClick}>{cta}</Btn>}
          {secondary && <Btn variant="ghost" onClick={secondaryOnClick}>{secondary}</Btn>}
        </div>
      )}
    </div>
  );
}

// Skeleton: shimmer placeholder for list/card surfaces during fetch.
// `w` and `h` accept any CSS dimension; `radius` defaults to small.
// The shimmer animation is keyframed in App.jsx <style> as @cg-shimmer.
export function Skeleton({ w = "100%", h = 14, radius = T_NEW.r1, style }) {
  return (
    <div className="cg-shimmer"
         style={{
           width: w, height: h, borderRadius: radius,
           background: `linear-gradient(90deg, ${T_NEW.ink08} 0%, ${T_NEW.ink12} 50%, ${T_NEW.ink08} 100%)`,
           backgroundSize: "200% 100%",
           ...style,
         }} />
  );
}

// SectionList: settings-style list rows (Page 33 / Page 25 sub-nav).
// Pass `items: [{label, sublabel?, right?, onClick?, danger?}]`.
export function SectionList({ items }) {
  return (
    <div style={{
      background: T_NEW.paperRaised,
      borderRadius: T_NEW.r3,
      border: `1px solid ${T_NEW.ink12}`,
      overflow: "hidden",
    }}>
      {items.map((it, i) => (
        <button key={i} onClick={it.onClick}
                style={{
                  display: "flex", alignItems: "center",
                  width: "100%", padding: "14px 16px",
                  background: "transparent",
                  border: "none",
                  borderTop: i > 0 ? `1px solid ${T_NEW.ink12}` : "none",
                  cursor: it.onClick ? "pointer" : "default",
                  textAlign: "left",
                  fontFamily: T_NEW.fontSans,
                  color: it.danger ? T_NEW.danger : T_NEW.ink,
                  transition: `background ${T_NEW.durBase} ${T_NEW.ease}`,
                }}
                onMouseEnter={(e) => { if (it.onClick) e.currentTarget.style.background = T_NEW.paperSoft; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: T_NEW.bodyLg, fontWeight: 500, color: "inherit" }}>{it.label}</div>
            {it.sublabel && (
              <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginTop: 2 }}>{it.sublabel}</div>
            )}
          </div>
          {it.right && <div style={{ color: T_NEW.ink56, fontSize: T_NEW.body, marginLeft: T_NEW.s3 }}>{it.right}</div>}
          {it.onClick && (
            <span aria-hidden style={{ marginLeft: T_NEW.s3, color: T_NEW.ink38, fontSize: T_NEW.body }}>›</span>
          )}
        </button>
      ))}
    </div>
  );
}
